import { useEffect, useMemo, useRef, useState } from 'react';
import type { Section } from '@pen/shared';
import { Rich } from '../ui/hooks';
import { observeActs, pauseEmbeds, type ActProgress } from './embed';
import { chapterAtTime, chapterLinks, parseFilmMessage, readFilmChapters, type FilmChapter, type FilmLink } from './film';

type Film = Extract<Section, { kind: 'film' }>;
type SafariElement = HTMLDivElement & { webkitRequestFullscreen?: () => void };

/** Lets the lesson drive the film (watch-this-part, back-to-the-film) without owning its iframe. */
export interface FilmHandle { seek(t: number): void; play(): void; element(): HTMLElement | null }
/** What the film tells the lesson. `onStop` only ever carries a checkpoint id the lesson really has. */
export interface FilmBridge {
  onChapters(links: FilmLink[]): void;
  onChapter(index: number): void;
  onStop(checkpointId: string): void;
  register(handle: FilmHandle | null): void;
}

export function FilmSection({ section, sections, onAct, bridge }: { section: Film; sections: Section[]; onAct(progress: ActProgress): void; bridge: FilmBridge }) {
  const frame = useRef<HTMLIFrameElement>(null);
  const viewport = useRef<SafariElement>(null);
  const callback = useRef(onAct);
  callback.current = onAct;
  const bridgeRef = useRef(bridge);
  bridgeRef.current = bridge;
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;
  const cleanup = useRef<(() => void) | undefined>(undefined);
  const [expanded, setExpanded] = useState(false);
  const checkpointIds = useMemo(() => new Set(sections.flatMap(s => s.kind === 'checkpoint' ? [s.checkpoint.id] : [])), [sections]);
  useEffect(() => () => cleanup.current?.(), [section.film]);
  useEffect(() => {
    const post = (message: object) => frame.current?.contentWindow?.postMessage(message, location.origin);
    bridgeRef.current.register({
      seek: t => post({ type: 'pen-film-seek', t }),
      play: () => post({ type: 'pen-film-play' }),
      element: () => viewport.current,
    });
    return () => bridgeRef.current.register(null);
  }, [section.film]);
  useEffect(() => {
    const childWindow = frame.current?.contentWindow;
    let previous = '';
    let chapters: FilmChapter[] = [];
    // The film's module scripts may not have run at iframe load, so chapters are read until the first message that finds them.
    const loadChapters = () => {
      if (chapters.length) return;
      let declared: unknown;
      try { declared = (frame.current?.contentWindow as (Window & { film?: { chapters?: unknown } }) | null)?.film?.chapters; }
      catch { return; }
      chapters = readFilmChapters(declared);
      if (chapters.length) bridgeRef.current.onChapters(chapterLinks(chapters, sectionsRef.current));
    };
    const message = (event: MessageEvent) => {
      loadChapters();
      const parsed = parseFilmMessage(event, { origin: location.origin, source: frame.current?.contentWindow, filmId: section.film, checkpointIds, chapterCount: chapters.length });
      if (!parsed) return;
      if (parsed.type === 'fullscreen') { setExpanded(true); return; }
      if (parsed.type === 'chapter') { bridgeRef.current.onChapter(parsed.index); return; }
      if (parsed.type === 'stop') {
        setExpanded(false);
        const leave = document.fullscreenElement ? document.exitFullscreen().catch(() => undefined) : Promise.resolve();
        void leave.then(() => bridgeRef.current.onStop(parsed.checkpointId));
        return;
      }
      const child = frame.current?.contentWindow as (Window & { film?: { acts?: { start: number }[] } }) | null;
      const acts = child?.film?.acts ?? [];
      if (!acts.every(act => Number.isFinite(act.start))) return;
      const act = chapterAtTime(acts, parsed.time);
      const ended = parsed.time >= parsed.duration && !parsed.playing;
      const signature = `${act}:${ended}`;
      if (signature === previous) return;
      previous = signature;
      callback.current({ act, ended });
    };
    window.addEventListener('message', message);
    // The film is idle until played, so it may send nothing for a while: poll for its chapters for the first two minutes.
    let ticks = 0;
    const poll = window.setInterval(() => {
      loadChapters();
      if (chapters.length || ++ticks >= 240) window.clearInterval(poll);
    }, 500);
    return () => {
      window.clearInterval(poll);
      window.removeEventListener('message', message);
      childWindow?.postMessage({ type: 'pen-film-pause' }, location.origin);
    };
  }, [section.film, checkpointIds]);
  useEffect(() => {
    if (!expanded) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') setExpanded(false); };
    window.addEventListener('keydown', escape);
    return () => { document.body.style.overflow = previous; window.removeEventListener('keydown', escape); };
  }, [expanded]);
  function fullscreen() {
    const element = viewport.current;
    if (!element) return;
    if (element.requestFullscreen && document.fullscreenEnabled) {
      void element.requestFullscreen().catch(() => setExpanded(true));
    } else if (element.webkitRequestFullscreen) {
      try { element.webkitRequestFullscreen(); } catch { setExpanded(true); }
    } else setExpanded(true); // iPad Safari versions without element fullscreen support.
  }
  const src = `/films/${section.film}/`;
  return <figure className="lesson-figure lesson-film">
    <h2 id={`${section.id}-title`}>{section.title}</h2>
    <div ref={viewport} className={`film-viewport${expanded ? ' film-expanded' : ''}`}>
      <iframe ref={frame} src={src} title={section.title} loading="lazy" allow="fullscreen" allowFullScreen aria-describedby={`${section.id}-caption`} onLoad={() => {
        cleanup.current?.();
        const doc = frame.current?.contentDocument;
        if (!doc) return;
        const stop = observeActs(doc, null, progress => callback.current(progress));
        cleanup.current = () => { stop(); pauseEmbeds(doc); };
      }} />
      {expanded && <button className="film-close secondary" type="button" onClick={() => setExpanded(false)}>Exit full screen</button>}
    </div>
    <div className="film-actions"><button className="secondary" type="button" onClick={fullscreen}>Full screen</button><a href={src} target="_blank" rel="noreferrer">Open film</a></div>
    <figcaption id={`${section.id}-caption`} className="section-caption"><Rich html={section.caption} /></figcaption>
  </figure>;
}
